import { createHash } from "node:crypto";
import {
  Cell,
  beginCell,
  external,
  storeMessage,
  storeStateInit,
} from "@ton/core";
import { mnemonicToPrivateKey, sign } from "@ton/crypto";
import { TonClient, WalletContractV4, fromNano, internal, toNano } from "@ton/ton";
import { BaseService, type FragmentContext } from "../core/context.js";
import {
  apiError,
  FragmentError,
  insufficientFundsError,
  validationError,
} from "../core/errors.js";
import { err, ok, type Result } from "../core/result.js";
import type {
  ConnectTonWalletData,
  GetBalanceParams,
  SendTonData,
  SendTonParams,
  SignTonProofParams,
  TonConnectAccount,
  TonConnectDevice,
  TonProof,
  WaitConfirmationData,
  WaitConfirmationParams,
  WalletAddress,
  WalletBalance,
} from "../types.js";

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const NANO = 1_000_000_000;
const TONCENTER_RPC = "https://toncenter.com/api/v2/jsonRPC";

/** Default app domain the TON Connect proof is bound to. */
const FRAGMENT_DOMAIN = "fragment.com";

/**
 * TON Connect device hint sent with `checkTonProofAuth` / confirm calls.
 * Fragment doesn't verify the contents, but the field must be present.
 */
export const DEFAULT_DEVICE: TonConnectDevice = {
  platform: "windows",
  appName: "tonkeeper",
  appVersion: "4.10.0",
  maxProtocolVersion: 2,
  features: [
    "SendTransaction",
    { name: "SendTransaction", maxMessages: 4 },
    { name: "SignData", types: ["text", "binary", "cell"] },
  ],
};

const sha256 = (buf: Buffer): Buffer => createHash("sha256").update(buf).digest();

/** v4r2 wallet transfers. */
export class V4R2Service extends BaseService {
  /**
   * Send TON from your v4r2 wallet, attaching an optional text comment.
   *
   * Requires `toncenterApiKey` and `walletSeed`. Performs a balance check
   * before broadcasting.
   *
   * Pass the amount **either** as `amount` (human TON, e.g. `0.21`) **or**
   * `amountNano` (exact nanoTON, e.g. Fragment's `msg.amount`). Prefer
   * `amountNano` when forwarding a Fragment payment — it's exact and avoids the
   * float-division footgun.
   *
   * @example
   * ```ts
   * // human amount
   * await client.ton.wallet.v4r2.send({ destinationAddress: "UQ...", amount: 0.21 });
   *
   * // exact Fragment payment — pass amount + payload straight from getPaymentInfo
   * await client.ton.wallet.v4r2.send({
   *   destinationAddress: msg.address,
   *   amountNano: msg.amount,            // "456100000"
   *   payloadCell: msg.payload,          // exact BoC cell, byte-matches the site
   * });
   * ```
   */
  async send({
    destinationAddress,
    amount,
    amountNano,
    payload = "",
    payloadCell,
  }: SendTonParams): Promise<Result<SendTonData>> {
    const { toncenterApiKey, walletSeed } = this.ctx.credentials;

    if (typeof destinationAddress !== "string" || destinationAddress.length === 0) {
      return err(validationError("`destinationAddress` must be a non-empty string."));
    }

    // Resolve the value to send as exact nanoTON (bigint) — no floats.
    const valueResult = resolveValueNano(amount, amountNano);
    if (!valueResult.ok) return valueResult;
    const valueNano = valueResult.data;

    // Body: an exact base64 BoC cell (preferred for Fragment payments — it is
    // byte-identical to what the website sends) or a plain text comment.
    let body: Cell | string = payload;
    if (payloadCell !== undefined) {
      try {
        body = Cell.fromBase64(payloadCell);
      } catch {
        return err(validationError("`payloadCell` is not a valid base64 BoC cell."));
      }
    }

    if (!toncenterApiKey) {
      return err(validationError("toncenterApiKey is not set."));
    }
    if (!walletSeed) {
      return err(validationError("walletSeed is not set."));
    }

    try {
      const client = new TonClient({
        endpoint: TONCENTER_RPC,
        apiKey: toncenterApiKey,
      });

      const mnemonics = walletSeed.trim().split(/\s+/);
      const keyPair = await mnemonicToPrivateKey(mnemonics);
      const wallet = WalletContractV4.create({
        workchain: 0,
        publicKey: keyPair.publicKey,
      });
      const contract = client.open(wallet);
      const sender = wallet.address.toString();

      const balanceNano = await contract.getBalance();

      if (balanceNano < valueNano) {
        return err(
          insufficientFundsError(
            `Insufficient funds. Required ${fromNano(valueNano)} TON, available ${fromNano(balanceNano)} TON.`,
            {
              details: {
                requiredNano: valueNano.toString(),
                availableNano: balanceNano.toString(),
              },
            },
          ),
        );
      }

      const seqno = await contract.getSeqno();
      // Sign the transfer body locally — we need the resulting cell separately
      // so we can serialize the outer external message and capture its base64
      // BoC. Fragment's `confirm_method` POST needs that BoC verbatim (see
      // `StarsService.confirmPayment`); without it, the TON tx broadcasts but
      // Fragment never credits the Stars.
      const transferBody = wallet.createTransfer({
        secretKey: keyPair.secretKey,
        seqno,
        messages: [
          internal({
            to: destinationAddress,
            value: valueNano,
            body,
            bounce: false,
          }),
        ],
      });

      const externalCell = beginCell()
        .store(
          storeMessage(
            external({ to: wallet.address, body: transferBody }),
          ),
        )
        .endCell();
      const boc = externalCell.toBoc().toString("base64");

      await client.sendFile(externalCell.toBoc());

      return ok({
        destination: destinationAddress,
        amount: Number(fromNano(valueNano)),
        amountNano: valueNano.toString(),
        payload: payloadCell ?? payload,
        sender,
        boc,
        balanceBefore: {
          nano: Number(balanceNano),
          ton: Number(fromNano(balanceNano)),
        },
      });
    } catch (e) {
      return err(
        new FragmentError("UNKNOWN", e instanceof Error ? e.message : "Failed to send TON.", {
          cause: e,
        }),
      );
    }
  }
}

/** Resolve `amount` (TON) / `amountNano` (nanoTON) into an exact bigint nano value. */
function resolveValueNano(
  amount: number | undefined,
  amountNano: string | bigint | undefined,
): Result<bigint> {
  if (amount !== undefined && amountNano !== undefined) {
    return err(
      validationError("Provide either `amount` or `amountNano`, not both."),
    );
  }
  if (amountNano !== undefined) {
    let v: bigint;
    try {
      v = BigInt(amountNano);
    } catch {
      return err(validationError("`amountNano` must be an integer (nanoTON)."));
    }
    if (v <= 0n) {
      return err(validationError("`amountNano` must be greater than 0."));
    }
    return ok(v);
  }
  if (amount !== undefined) {
    if (!Number.isFinite(amount) || amount <= 0) {
      return err(validationError("`amount` must be greater than 0."));
    }
    return ok(toNano(amount.toString()));
  }
  return err(validationError("Provide `amount` (TON) or `amountNano` (nanoTON)."));
}

/** TON wallet operations: balance + nested v4r2 transfers. */
export class WalletService extends BaseService {
  readonly v4r2: V4R2Service;

  constructor(ctx: FragmentContext) {
    super(ctx);
    this.v4r2 = new V4R2Service(ctx);
  }

  /**
   * Derive your wallet's v4r2 address from the configured `walletSeed` — no
   * network. Compare `raw` with a Fragment `transaction.from` to confirm the
   * payment will originate from the wallet the order expects.
   *
   * @example
   * ```ts
   * const me = await client.ton.wallet.getAddress();
   * if (me.ok) console.log(me.data.friendly, me.data.raw);
   * ```
   */
  async getAddress(): Promise<Result<WalletAddress>> {
    const { walletSeed } = this.ctx.credentials;
    if (!walletSeed) return err(validationError("walletSeed is not set."));
    try {
      const keyPair = await mnemonicToPrivateKey(walletSeed.trim().split(/\s+/));
      const wallet = WalletContractV4.create({
        workchain: 0,
        publicKey: keyPair.publicKey,
      });
      return ok({
        friendly: wallet.address.toString(),
        raw: wallet.address.toRawString(),
      });
    } catch (e) {
      return err(
        new FragmentError("VALIDATION", "Invalid walletSeed.", { cause: e }),
      );
    }
  }

  /**
   * Build the TON Connect-style account JSON Fragment expects in the
   * post-broadcast confirm call (see {@link StarsService.confirmPayment}).
   *
   * The shape matches what `tonConnectUI.wallet.account` carries on
   * fragment.com — raw address, hex public key, mainnet chain, and the BoC of
   * the wallet's StateInit.
   *
   * @example
   * ```ts
   * const account = await client.ton.wallet.getAccount();
   * if (account.ok) console.log(account.data.address);
   * ```
   */
  async getAccount(): Promise<Result<TonConnectAccount>> {
    const { walletSeed } = this.ctx.credentials;
    if (!walletSeed) return err(validationError("walletSeed is not set."));
    try {
      const keyPair = await mnemonicToPrivateKey(walletSeed.trim().split(/\s+/));
      const wallet = WalletContractV4.create({
        workchain: 0,
        publicKey: keyPair.publicKey,
      });
      const stateInitCell = beginCell()
        .store(
          storeStateInit({
            code: wallet.init.code,
            data: wallet.init.data,
          }),
        )
        .endCell();
      return ok({
        address: wallet.address.toRawString(),
        publicKey: keyPair.publicKey.toString("hex"),
        chain: "-239",
        walletStateInit: stateInitCell.toBoc().toString("base64"),
      });
    } catch (e) {
      return err(
        new FragmentError("VALIDATION", "Invalid walletSeed.", { cause: e }),
      );
    }
  }

  /**
   * Sign a TON Connect `ton_proof` challenge with your `walletSeed`.
   *
   * Wallet-side of Fragment's TON Connect login. Normally you don't call this
   * directly — {@link connectTonWallet} scrapes the challenge and runs the whole
   * handshake for you.
   */
  async signTonProof({
    payload,
    domain = FRAGMENT_DOMAIN,
  }: SignTonProofParams): Promise<Result<TonProof>> {
    if (typeof payload !== "string" || payload.length === 0) {
      return err(validationError("`payload` (ton_proof challenge) is required."));
    }
    const { walletSeed } = this.ctx.credentials;
    if (!walletSeed) return err(validationError("walletSeed is not set."));
    try {
      const keyPair = await mnemonicToPrivateKey(walletSeed.trim().split(/\s+/));
      const wallet = WalletContractV4.create({
        workchain: 0,
        publicKey: keyPair.publicKey,
      });
      const stateInit = beginCell()
        .store(storeStateInit({ code: wallet.init.code, data: wallet.init.data }))
        .endCell()
        .toBoc()
        .toString("base64");

      const timestamp = Math.floor(Date.now() / 1000);
      const domainBuf = Buffer.from(domain, "utf8");
      const wc = Buffer.alloc(4);
      wc.writeInt32BE(wallet.address.workChain);
      const domainLen = Buffer.alloc(4);
      domainLen.writeUInt32LE(domainBuf.length);
      const ts = Buffer.alloc(8);
      ts.writeBigUInt64LE(BigInt(timestamp));

      // TON Connect ton_proof message (ton-connect spec):
      //   "ton-proof-item-v2/" ++ workchain(int32 BE) ++ addrHash(32) ++
      //   domainLen(uint32 LE) ++ domain ++ timestamp(uint64 LE) ++ payload
      const message = Buffer.concat([
        Buffer.from("ton-proof-item-v2/", "utf8"),
        wc,
        wallet.address.hash,
        domainLen,
        domainBuf,
        ts,
        Buffer.from(payload, "utf8"),
      ]);
      // signature = ed25519( sha256( 0xffff ++ "ton-connect" ++ sha256(message) ) )
      const signInput = sha256(
        Buffer.concat([
          Buffer.from([0xff, 0xff]),
          Buffer.from("ton-connect", "utf8"),
          sha256(message),
        ]),
      );
      const signature = sign(signInput, keyPair.secretKey).toString("base64");

      return ok({
        timestamp,
        domain: { lengthBytes: domainBuf.length, value: domain },
        signature,
        payload,
        state_init: stateInit,
      });
    } catch (e) {
      return err(
        new FragmentError("VALIDATION", "Invalid walletSeed.", { cause: e }),
      );
    }
  }

  /**
   * Establish a **fully-proven** TON Connect session with Fragment from your
   * `walletSeed`, and store the fresh `stel_ton_token` on the client.
   *
   * Fragment accepts an ordinary `stel_ton_token` for *creating* Stars orders,
   * but it only **credits** a purchase when the paying wallet's TON Connect
   * session was proven this session (`checkTonProofAuth` → `verified: true`) —
   * the same handshake fragment.com runs on every page load. Without it the TON
   * debits but Stars never arrive. {@link StarsService.purchase} calls this
   * automatically; call it yourself before a manual buy flow.
   *
   * @example
   * ```ts
   * const c = await client.ton.wallet.connectTonWallet();
   * if (c.ok && c.data.verified) console.log("session proven ✅");
   * ```
   */
  async connectTonWallet({
    url = "https://fragment.com/",
  }: { url?: string } = {}): Promise<Result<ConnectTonWalletData>> {
    const { walletSeed } = this.ctx.credentials;
    if (!walletSeed) return err(validationError("walletSeed is not set."));

    // 1) Scrape the `ton_proof` challenge Fragment embeds in its page state.
    const page = await this.ctx.http.getText(url, {
      headers: this.ctx.htmlHeaders(),
    });
    if (!page.ok) return page;

    // Keep the session hash fresh while we're on the page.
    const hashMatch = page.data.match(/api\?hash=([0-9a-f]{16,})/i);
    if (hashMatch?.[1]) this.ctx.update({ hash: hashMatch[1] });

    const proofMatch = page.data.match(/"ton_proof":"([^"]*)"/);
    const payload = proofMatch?.[1];
    if (!payload) {
      return err(
        new FragmentError(
          "AUTH",
          "No `ton_proof` challenge found on the page — check your stel_* cookies and that a wallet is connected on fragment.com.",
        ),
      );
    }

    // 2) Build the account JSON + sign the proof.
    const account = await this.getAccount();
    if (!account.ok) return account;
    const proof = await this.signTonProof({ payload });
    if (!proof.ok) return proof;

    // 3) POST checkTonProofAuth — Fragment returns {verified} + a Set-Cookie
    //    carrying a freshly-proven stel_ton_token.
    const res = await this.ctx.http.postFormRaw<{
      verified?: boolean;
      error?: string;
    }>(
      this.ctx.apiUrl(),
      {
        account: JSON.stringify(account.data),
        device: JSON.stringify(DEFAULT_DEVICE),
        proof: JSON.stringify(proof.data),
        method: "checkTonProofAuth",
      },
      { headers: this.ctx.apiHeaders() },
    );
    if (!res.ok) return res;

    const { data: body, setCookies } = res.data;
    if (!body.verified) {
      return err(
        apiError(body.error ?? "Fragment rejected the TON proof (verified=false).", {
          details: body,
        }),
      );
    }

    // 4) Capture and store the fresh stel_ton_token so later calls send it.
    let tonToken: string | undefined;
    for (const cookie of setCookies) {
      const m = cookie.match(/(?:^|\s)stel_ton_token=([^;]+)/);
      if (m?.[1] && m[1] !== "DELETED") {
        tonToken = m[1];
        break;
      }
    }
    if (tonToken) this.ctx.update({ stelTonToken: tonToken });

    return ok({ verified: true, tonToken });
  }

  /**
   * Poll toncenter until the broadcast external message lands in a confirmed
   * transaction — the same wait the working Go client does before it considers a
   * purchase done. Fragment credits Stars off the confirmed on-chain payment, so
   * this both confirms success and gives Fragment's watcher time to see it.
   *
   * @example
   * ```ts
   * const tx = await client.ton.wallet.v4r2.send({ ... });
   * if (tx.ok) await client.ton.wallet.waitForConfirmation({ boc: tx.data.boc });
   * ```
   */
  async waitForConfirmation({
    boc,
    address,
    timeoutMs = 90_000,
    pollIntervalMs = 5_000,
  }: WaitConfirmationParams): Promise<Result<WaitConfirmationData>> {
    const { toncenterApiKey } = this.ctx.credentials;
    if (!toncenterApiKey) {
      return err(validationError("toncenterApiKey is not set."));
    }
    let wantHash: string;
    try {
      wantHash = Cell.fromBase64(boc).hash().toString("base64");
    } catch {
      return err(validationError("`boc` is not a valid base64 BoC cell."));
    }

    let sender = address;
    if (!sender) {
      const addr = await this.getAddress();
      if (!addr.ok) return addr;
      sender = addr.data.raw;
    }

    const deadline = Date.now() + timeoutMs;
    let firstPoll = true;
    while (Date.now() < deadline) {
      if (!firstPoll) await sleep(pollIntervalMs);
      firstPoll = false;

      const res = await this.ctx.http.getJson<{
        ok?: boolean;
        result?: Array<{
          transaction_id?: { hash?: string };
          in_msg?: { hash?: string };
        }>;
      }>("https://toncenter.com/api/v2/getTransactions", {
        params: { address: sender, limit: 12 },
        headers: { "X-API-Key": toncenterApiKey },
      });
      // Transient errors (rate limits etc.) — keep polling until the deadline.
      if (res.ok) {
        for (const tx of res.data.result ?? []) {
          if (tx.in_msg?.hash === wantHash) {
            return ok({ txHash: tx.transaction_id?.hash ?? "" });
          }
        }
      }
    }

    return err(
      new FragmentError(
        "NETWORK",
        `Timed out after ${timeoutMs}ms waiting for the TON transfer to confirm on-chain.`,
      ),
    );
  }

  /**
   * Check a TON wallet balance. Uses toncenter when `toncenterApiKey` is set,
   * otherwise tonconsole (tonapi.io) when `tonconsoleApiKey` is set.
   *
   * @example
   * ```ts
   * const res = await client.ton.wallet.getBalance({ address: "UQ..." });
   * if (res.ok) console.log(res.data.ton);
   * ```
   */
  async getBalance({
    address,
  }: GetBalanceParams): Promise<Result<WalletBalance>> {
    if (typeof address !== "string" || address.length === 0) {
      return err(validationError("`address` must be a non-empty string."));
    }

    const { toncenterApiKey, tonconsoleApiKey } = this.ctx.credentials;
    if (toncenterApiKey) return this.balanceFromToncenter(address);
    if (tonconsoleApiKey) return this.balanceFromTonconsole(address);
    return err(
      validationError(
        "No API key set. Provide toncenterApiKey or tonconsoleApiKey.",
      ),
    );
  }

  private async balanceFromToncenter(
    address: string,
  ): Promise<Result<WalletBalance>> {
    const res = await this.ctx.http.getJson<{
      ok?: boolean;
      result?: string;
      error?: string;
    }>("https://toncenter.com/api/v2/getAddressBalance", {
      params: { address },
      headers: { "X-API-Key": this.ctx.credentials.toncenterApiKey },
    });
    if (!res.ok) return res;

    if (res.data.ok && res.data.result !== undefined) {
      const nano = Number.parseInt(res.data.result, 10);
      return ok({ nano, ton: nano / NANO, source: "toncenter" });
    }
    return err(toApiError(res.data.error ?? "Unknown toncenter error", res.data));
  }

  private async balanceFromTonconsole(
    address: string,
  ): Promise<Result<WalletBalance>> {
    const res = await this.ctx.http.getJson<{ balance?: number | string }>(
      `https://tonapi.io/v2/accounts/${encodeURIComponent(address)}`,
      { headers: { Authorization: `Bearer ${this.ctx.credentials.tonconsoleApiKey}` } },
    );
    if (!res.ok) return res;

    if (res.data.balance !== undefined) {
      const nano =
        typeof res.data.balance === "string"
          ? Number.parseInt(res.data.balance, 10)
          : res.data.balance;
      return ok({ nano, ton: nano / NANO, source: "tonconsole" });
    }
    return err(toApiError("Balance not found in tonconsole response", res.data));
  }
}

function toApiError(message: string, details: unknown): FragmentError {
  return new FragmentError("API", message, { details });
}
